import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Already signed in (and verified) — no reason to show the form.
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header — wordmark linked to getboby.ai; onError hides if the asset is missing (Wisdom §4) */}
        <div className="text-center mb-8">
          <a href="https://getboby.ai" target="_blank" rel="noopener noreferrer">
            <img
              src="/BobyWordmark-PinLogoInGold.png"
              alt="Boby"
              className="h-12 mx-auto mb-4"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          </a>
          <h1 className="text-xl font-semibold text-gray-800">Firms Portal</h1>
          <p className="text-gray-500 text-sm">Security Firm Management</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-boby-lg shadow-sm border border-gray-100 p-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@firm.com"
                autoComplete="email"
                disabled={isSubmitting}
                className="w-full rounded-boby-sm border border-gray-300 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-boby-gold disabled:opacity-60"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
                disabled={isSubmitting}
                className="w-full rounded-boby-sm border border-gray-300 px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-boby-gold disabled:opacity-60"
              />
            </div>

            {error && (
              <p role="alert" className="text-sm text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full rounded-boby-sm bg-boby-gold py-2 font-semibold text-gray-900 transition hover:brightness-95 disabled:opacity-60"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in'}
            </button>
          </form>

          <p className="mt-6 pt-4 border-t border-gray-100 text-center text-sm text-gray-500">
            Sign in with your Peeler credentials.
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-8">
          &copy; {new Date().getFullYear()} BOBY Security Information Service
        </p>
      </div>
    </div>
  );
}
